import React from 'react';
import { AppTable } from '@allygpo/ally-ux';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { Checkbox, alignEnd, headerEnd, tableView, unpaginated } from '../../components/ui';
import { ExcludedOpportunity } from '../../types';
import { shortDate } from '../../utils/format';

/** What the Excluded opportunities cells read from `meta.view`. */
interface ExcludedView {
  isSelectable: (opp: ExcludedOpportunity) => boolean;
  isChecked: (opp: ExcludedOpportunity) => boolean;
  onToggle: (opp: ExcludedOpportunity, include: boolean) => void;
  /** Wraps a cell's content in the row's tooltip: why it isn't modeled, or why it can't be checked. */
  withTooltip: (content: React.ReactNode, opp: ExcludedOpportunity) => React.ReactNode;
}

const formatOppRebate = (opp: ExcludedOpportunity) => {
  if (opp.rebate == null) return '—';
  const rebateNum = Number(opp.rebate);
  if (isNaN(rebateNum)) return '—';
  return opp.paid_on?.toLocaleLowerCase().trim() === '$ per unit'
    ? `$${rebateNum.toFixed(2)}`
    : `${(rebateNum * 100).toFixed(2)}%`;
};

const formatDate = (value?: Date) => (value ? shortDate(value) : '—');

/** A text column whose content carries the row's tooltip. */
const textColumn = (
  id: string,
  header: ColumnDef<ExcludedOpportunity>['header'],
  size: number,
  value: (opp: ExcludedOpportunity) => React.ReactNode,
  extra: Partial<ColumnDef<ExcludedOpportunity>> = {},
): ColumnDef<ExcludedOpportunity> => ({
  id,
  size,
  header,
  cell: ({ row: { original: opp }, table }) => tableView<ExcludedView, ExcludedOpportunity>(table).withTooltip(value(opp), opp),
  ...extra,
});

// Sizes total about the modal's inner width, so the fixed layout needs no horizontal scroll.
const excludedColumns: ColumnDef<ExcludedOpportunity>[] = [
  {
    id: 'include',
    size: 64,
    header: () => <span className="flex justify-content-center">Include</span>,
    cell: ({ row: { original: opp }, table }) => {
      const { isSelectable, isChecked, onToggle } = tableView<ExcludedView, ExcludedOpportunity>(table);
      return (
        <span className="mix-center flex justify-content-center text-center">
          <Checkbox checked={isChecked(opp)} disabled={!isSelectable(opp)} onChange={(next) => onToggle(opp, next)} ariaLabel={`Include ${opp.opportunity ?? 'opportunity'}`} />
        </span>
      );
    },
  },
  textColumn('area', 'Therapeutic area', 105, (opp) => opp.category),
  textColumn('product', 'Product', 95, (opp) => opp.product_family_name),
  textColumn('opportunity', 'Opportunity', 175, (opp) => <span className="font-medium">{opp.opportunity}</span>),
  textColumn('trigger', 'Trigger type', 100, (opp) => opp.trigger_type),
  textColumn('start', 'Start date', 85, (opp) => formatDate(opp.co_start_date)),
  textColumn('end', 'End date', 85, (opp) => formatDate(opp.co_end_date)),
  textColumn('tier', () => headerEnd('Tier'), 50, (opp) => opp.tier, { meta: alignEnd }),
  textColumn('rebate', () => headerEnd('Rebate'), 75, formatOppRebate, { meta: alignEnd }),
  textColumn('paidOn', 'Paid on', 90, (opp) => opp.paid_on, { meta: { headerStyle: { paddingInlineStart: 'var(--a-spacing-5)' }, cellStyle: { paddingInlineStart: 'var(--a-spacing-5)' } } }),
];

interface ExcludedOpportunitiesTableProps extends ExcludedView {
  rows: ExcludedOpportunity[];
}

/** The Excluded opportunities modal's table: check a rebate to include it in this run. */
const ExcludedOpportunitiesTable: React.FC<ExcludedOpportunitiesTableProps> = ({ rows, isSelectable, isChecked, onToggle, withTooltip }) => {
  const table = useReactTable({
    ...unpaginated,
    data: rows,
    columns: excludedColumns,
    meta: { view: { isSelectable, isChecked, onToggle, withTooltip } satisfies ExcludedView },
    getRowId: (opp, index) => opp.unique_id ?? String(index),
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <AppTable
      isBordered={false}
      size="sm"
      ariaLabel="Excluded opportunities"
      table={table}
      rowClassName={({ rowData }) => (isSelectable(rowData) ? '' : 'is-off')}
      messages={{ emptyStateText: 'No excluded opportunities found.' }}
    />
  );
};

export default ExcludedOpportunitiesTable;
